import { PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import { recalculateBuilderCost } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import { getAccuracy } from "utilities/number/number";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    type ICostLineItem,
    isMarkupLineItem,
    LineItemConstants,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { LineItemColumns } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import { type TaxMethod } from "entity/tax/common/tax.types";

import "./CellImplementations.less";

export interface IUnitCostCellProps extends ICostLineItem, IPreviewLineItemProps {
    ownerPriceLocked?: boolean;
    ellipsis?: boolean;
    totalEffectiveRate?: number;
    taxMethod?: TaxMethod;
    isStackedLineItem?: boolean;
    removeTrailingZerosOnEdit?: boolean;
    debouncingContext?: IInputDebouncingContext;
}

export class UnitCostCell extends PureComponent<IGridCellRenderProps<IUnitCostCellProps>> {
    private handleChange = (_: string, unitCost?: number) => {
        const { cellValue: lineItem, onChange, field } = this.props;

        if (lineItem) {
            const isClientPriceLocked = lineItem.ownerPriceLocked;

            if (isMarkupLineItem(lineItem)) {
                onChange(field.path, {
                    ...lineItem,
                    ...recalculateAllFields(
                        {
                            ...lineItem,
                            unitCost: unitCost ?? 0,
                        },
                        isClientPriceLocked,
                    ),
                    unitCost: unitCost ?? 0,
                });
            } else {
                recalculateBuilderCost(
                    { ...lineItem, unitCost: unitCost ?? 0 },
                    field.path,
                    onChange,
                    isClientPriceLocked,
                    lineItem?.totalEffectiveRate,
                    lineItem?.taxMethod,
                );
            }
        }
    };

    private handleBlur = (field: string) => {
        this.props.setRowTouched!(this.props.rowIndex!, [field, "ownerPrice", "builderCost"], true);
    };

    render() {
        const { cellValue, isInEdit, field, canEdit, autoFocus, onFocus } = this.props;
        const readOnly = !isInEdit || !canEdit;

        let component: JSX.Element;
        if (cellValue?.isStackedLineItem) {
            component = <ValueDisplay value={null} />; // will show "--" if it's a stacked line item
        } else {
            const decimalScale = readOnly ? Math.max(2, getAccuracy(cellValue?.unitCost)) : 4;
            component = (
                <LineItemPreview
                    fieldName="unitCost"
                    addPreviewListener={cellValue?.addPreviewListener}
                    render={(preview) => (
                        <Currency<undefined>
                            id={field.full}
                            min={LineItemConstants.Currency.MIN}
                            max={LineItemConstants.Currency.MAX}
                            data-testid={field.full}
                            value={preview?.unitCost ?? cellValue?.unitCost ?? undefined}
                            readOnly={readOnly}
                            onChange={this.handleChange}
                            onBlur={() => this.handleBlur(field.name)}
                            decimalScale={decimalScale}
                            autoFocus={autoFocus}
                            shouldTriggerChangeOnEmptyInput={false}
                            removeTrailingZerosOnEdit={cellValue?.removeTrailingZerosOnEdit}
                            className={preview ? "LineItemPreview" : undefined}
                            debouncingContext={
                                cellValue?.debouncingContext ?? {
                                    useDebouncing: true,
                                    inputDelay: 150,
                                }
                            }
                        />
                    )}
                />
            );
        }

        if (isInEdit) {
            return (
                <BTFormItemAutomatic
                    id={field.full}
                    className={cn(
                        "LineItemCell",
                        { RightAlignFormLabel: !!readOnly },
                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {component}
                </BTFormItemAutomatic>
            );
        }

        return (
            <div
                className={cn(
                    "LineItemCell",
                    { lineItemEditableCell: canEdit },
                    { ellipsis: cellValue?.ellipsis },
                )}
                tabIndex={canEdit ? 0 : undefined}
                onFocus={onFocus}
            >
                {component}
            </div>
        );
    }
}

export const unitCostCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.UnitCost,
        name: "Unit cost",
        jsonKey: "unitCost",
        width: 150,
        minWidth: 48,
        ellipsis: true,
        align: "right",
        isSortable: true,
        columnType: GridLineItemColumnType.flatCurrency,
        isFrozen: false,
        isHidden: false,
        Cell: UnitCostCell,
    }) as Partial<ILineItemColumn<T, IUnitCostCellProps>>;
